import type { AfterViewInit, ElementRef } from '@angular/core';
import { ChangeDetectionStrategy, Component, input, signal, ViewChild } from '@angular/core';
import { google } from 'google-maps';
import { PalantirSkeletonComponent } from '../skeleton/skeleton.component';
import type {
  PalantirMapAvatarMarker,
  PalantirMapCenter,
  PalantirMapCustomerMarker,
  PalantirMapMarker,
} from './map.type';

@Component({
  selector: 'palantir-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [PalantirSkeletonComponent],
})
export class PalantirMapComponent implements AfterViewInit {
  @ViewChild('map') public mapRef!: ElementRef<HTMLElement>;

  public key = input.required<string>();

  public center = input.required<PalantirMapCenter>();

  public zoom = input.required<number>();

  public mapId = input<string>();

  public markers = input<PalantirMapMarker[]>();

  public isLoading = signal<boolean>(true);

  public map = signal<google.maps.Map | undefined>(undefined);

  public async ngAfterViewInit(): Promise<void> {
    this.isLoading.set(true);

    await this.loadGoogleMaps();

    const mapElement = this.mapRef.nativeElement;

    this.map.set(
      new google.maps.Map(mapElement, {
        disableDefaultUI: true,
        fullscreenControl: true,
        center: { lat: this.center().latitude, lng: this.center().longitude },
        zoom: this.zoom(),
        mapId: this.mapId(),
      }),
    );

    const bounds = new google.maps.LatLngBounds();

    for (const marker of this.markers() ?? []) {
      if (marker.type === 'AVATAR') {
        await this.setAvatarItem(marker);

        bounds.extend(new google.maps.LatLng(marker.latitude, marker.longitude));
      }

      if (marker.type === 'CUSTOMER') {
        await this.setCustomerItem(marker);

        bounds.extend(new google.maps.LatLng(marker.latitude, marker.longitude));
      }
    }

    if (this.markers()?.length) {
      this.map()?.fitBounds(bounds);
    }

    this.isLoading.set(false);
  }

  private async setAvatarItem(item: PalantirMapAvatarMarker): Promise<void> {
    const marker = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

    const image = new Image();
    image.src = item.url ?? '';
    image.style.width = '30px';
    image.style.height = '30px';
    image.style.borderRadius = '50%';
    image.style.border = '1px solid rgba(0, 0, 0, 0.5)';
    image.style.boxShadow = '0 0 2px 0 rgba(0, 0, 0, 0.5)';
    image.style.transform = 'translateY(50%)';

    new marker.AdvancedMarkerElement({
      position: new google.maps.LatLng(item.latitude, item.longitude),
      map: this.map(),
      content: image,
      zIndex: item.zIndex,
    });
  }

  private async setCustomerItem(item: PalantirMapCustomerMarker): Promise<void> {
    const marker = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

    const image = new Image();
    image.src =
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGAAAABgCAYAAADimHc4AAAAAXNSR0IArs4c6QAAApZJREFUeF7tnEFuwjAQRWHRPfdr79L2Lu392LOgKlKlBlX5HTw/44keGxZj/zjv2U4IguOBVymBY+nROfgBAcWTAAEIKCZQfHhWAAKKCRQfnhWAgMcJnF4ub9+9zx9Pt/eOr7Yr4Ab/eni9QT8e3rtKaClgAf9n2jeV0E7An/AbS2glYBV+UwltBPwLfkMJLQSE4DeTML2Ah+A3kjC1gCH4TSRMKyAFfgMJUwpIhT+5hOkEWOBPLGE+Ac+X69oznfPn0+qYT4P9t36ehICtid8dDwEIWBIY3UJG+2/tgxWwNXG2oPWL+NY+WAFbE2cFNF8B6iJXPKHk4dXnCBmQ3CC8BSEg1wACcnmG0xAQRpbbAQG5PMNpCAgjy+2AgFye4TQEhJHldkgXoO6zR29jR/NV/1y8Og0BmpG1BQKseHU4AjQjawsEWPHqcARoRtYWCLDi1eEI0IysLdIFWEfbIDz6OQMByVIRkAw0GoeAKLHk9ghIBhqNQ0CUWHJ7BCQDjcYhIEosuX25ADWA6u8DFO/R8av+98dP/xygBoCApQIE3E3J0Qmk+rMCxB6kAKoVrPojAAHrP8JzXyTd+awA8StJBBQD2r0AdYJ7r5dvQXsHrM4PAYqQuY4AM2AVjwBFyFxHgBmwikeAImSuI8AMWMWXC1ADUA+zRk+wOl+dPw/jeBjHw7jfc4AvZPhCRu36y7raY7kGDP6pntKBgMF/JayeoW7BaoJwF7T3uyA1w/ZeL18Beweszg8BipC5jgAzYBWPAEXIXEeAGbCKR4AiZK4jwAxYxdsFqAFQjxEIPw2NxdNaEUCAImSuI8AMWMUjQBEy1xFgBqziEaAImesIMANW8QhQhMx1BJgBq3gEKELmOgLMgFX8F9ywZn/bLD6pAAAAAElFTkSuQmCC';
    image.style.width = '30px';
    image.style.height = '30px';
    image.style.borderRadius = '50%';
    image.style.transform = 'translateY(50%)';

    new marker.AdvancedMarkerElement({
      position: new google.maps.LatLng(item.latitude, item.longitude),
      map: this.map(),
      content: image,
      zIndex: item.zIndex,
    });
  }

  private async loadGoogleMaps(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.key()) {
        reject(new Error('Google Maps API key is required'));
        return;
      }

      const scriptAlreadyLoaded = document.querySelector('#google-maps-script');
      if (scriptAlreadyLoaded) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.type = 'text/javascript';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.key()}`;
      script.defer = true;
      script.async = true;
      script.onload = () => resolve();
      document.body.append(script);
    });
  }
}
